import React from "react";
import {
  CONFIRMATION_TYPES,
  TRIGGER_TYPES,
  TRIGGER_TYPES_DISPLAY_MAPPING,
} from "Components/common/constants";
import {
  TimelineActionItem,
  TimelineActionsList,
  TimelineContainer,
  TimelineEmptyState,
  TimelineHexagonNode,
  TimelineNodeLabel,
  TimelineNodeRow,
  TimelineText,
  TimelineTitle,
  TimelineVerticalLine,
  TimelineWrapper,
} from "./styles";
import { ReactComponent as StopShapeIcon } from "./assets/StopShape.svg";

interface MissionTimelineProps {
  stops: any[];
  getStopName: (stopId: string) => string;
  loading?: boolean;
}

// Utility functions for better testability
export const normalizeVdaActions = (stop: any): any[] => {
  return (
    stop?.missionStopVdaActions || stop?.vda_actions || stop?.vdaActions || []
  );
};

export const formatDurationAction = (
  actionText: string,
  duration?: string | number
): string => {
  if (duration) {
    return `${actionText} (Trigger: Duration, Countdown: ${duration} second${
      parseInt(duration.toString()) > 1 ? "s" : ""
    })`;
  }
  return `${actionText} (Trigger: Duration)`;
};

export const formatTriggerTypeAction = (
  actionText: string,
  triggerType: string
): string => {
  if (
    triggerType &&
    TRIGGER_TYPES_DISPLAY_MAPPING[
      triggerType as keyof typeof TRIGGER_TYPES_DISPLAY_MAPPING
    ]
  ) {
    return `${actionText} (Trigger: ${
      TRIGGER_TYPES_DISPLAY_MAPPING[
        triggerType as keyof typeof TRIGGER_TYPES_DISPLAY_MAPPING
      ]
    })`;
  }
  return `${actionText} (Trigger: ${triggerType})`;
};

export const formatActionParameters = (
  actionText: string,
  actionParameters: any
): string => {
  if (
    !actionParameters ||
    typeof actionParameters !== "object" ||
    Array.isArray(actionParameters) ||
    Object.keys(actionParameters).length === 0
  ) {
    return actionText;
  }

  const paramStrings = Object.entries(actionParameters)
    .map(([key, value]) => {
      // Skip null, undefined, or empty values
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if ((key === "triggerType" || key === "trigger_type") && value) {
        return `Trigger: ${
          TRIGGER_TYPES_DISPLAY_MAPPING[
            value as keyof typeof TRIGGER_TYPES_DISPLAY_MAPPING
          ] || value
        }`;
      }
      if (key === "duration" && value) {
        return `Countdown: ${value} second${
          parseInt(value.toString()) > 1 ? "s" : ""
        }`;
      }
      // Skip displaying the order field as it's internal
      if (key === "order") {
        return null;
      }
      return `${key}: ${value}`;
    })
    .filter(Boolean);

  if (paramStrings.length > 0) {
    return `${actionText} (${paramStrings.join(", ")})`;
  }

  return actionText;
};

export const processVdaAction = (vdaAction: any): string | null => {
  const actionDescription =
    vdaAction?.actionDescription || vdaAction?.action_description;
  const actionType = vdaAction?.actionType || vdaAction?.action_type;

  if (!actionDescription && !actionType) {
    return null;
  }

  const actionText = actionDescription || actionType || "Unknown Action";
  const actionParameters =
    vdaAction.actionParameters || vdaAction.action_parameters;

  if (!actionParameters || actionParameters.length === 0) {
    return actionText;
  }

  const firstParam = actionParameters[0];
  const triggerType = firstParam?.triggerType || firstParam?.trigger_type;
  const duration = firstParam?.duration;
  const timeValue = duration;

  // Handle specific trigger types
  if (triggerType === TRIGGER_TYPES.DURATION) {
    return formatDurationAction(actionText, timeValue || undefined);
  } else if (triggerType) {
    return formatTriggerTypeAction(actionText, triggerType);
  }

  // Use formatActionParameters for any other parameters
  return formatActionParameters(actionText, firstParam || {});
};

export const extractActionsFromStop = (stop: any): string[] => {
  const actions: string[] = [];

  // Check for double confirmation first
  if (
    stop.confirmationType === CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
  ) {
    actions.push("Double Confirmation");
    return actions; // Return early, no other actions should be present
  }

  const vdaActions = normalizeVdaActions(stop);

  if (vdaActions && vdaActions.length > 0) {
    vdaActions.forEach((vdaAction: VdaAction) => {
      const actionText = processVdaAction(vdaAction);
      if (actionText) {
        actions.push(actionText);
      }
    });
  }

  /**
   * MissionPlanner's HitchTypeEnum is currently:
   *
   * UNSET = 0
   * HITCH = 1
   * UNHITCH = 2
   */
  const hitchTypeSet: boolean = stop.hitchType || stop.hitch_type;

  if (hitchTypeSet) {
    actions.push("Un-Hitch");
  }

  return actions;
};

export const resolveStopName = (
  stop: any,
  getStopNameFn: (stopId: string) => string
): string => {
  if (stop.name) {
    return stop.name;
  }
  if (stop.stopPk) {
    return getStopNameFn(stop.stopPk);
  }
  if (stop.stopId) {
    return getStopNameFn(stop.stopId);
  }
  if (stop.id) {
    return getStopNameFn(stop.id);
  }

  return "Unknown Stop";
};

export const MissionTimeline = ({
  stops = [],
  getStopName,
  loading = false,
}: MissionTimelineProps) => {
  if (loading) {
    return (
      <TimelineWrapper>
        <TimelineTitle>Mission Details</TimelineTitle>
        <TimelineContainer>
          <TimelineEmptyState>Loading stops...</TimelineEmptyState>
        </TimelineContainer>
      </TimelineWrapper>
    );
  }

  // If no stops, show empty message or timeline
  if (!stops || stops.length === 0) {
    return (
      <TimelineWrapper>
        <TimelineTitle>Mission Details</TimelineTitle>
        <TimelineContainer>
          <TimelineEmptyState>No stops planned</TimelineEmptyState>
        </TimelineContainer>
      </TimelineWrapper>
    );
  }

  return (
    <TimelineWrapper>
      <TimelineTitle>Mission Details</TimelineTitle>
      <TimelineContainer>
        <TimelineVerticalLine />
        {stops.map((stop, index) => {
          const stopName = resolveStopName(stop, getStopName);
          const actions = extractActionsFromStop(stop);

          return (
            <TimelineNodeRow key={`${stopName}-${index}`}>
              <TimelineHexagonNode>
                <StopShapeIcon />
              </TimelineHexagonNode>
              <TimelineText>
                <TimelineNodeLabel>{stopName}</TimelineNodeLabel>
                <TimelineActionsList>
                  <TimelineActionItem>
                    {actions.map((action, actionIndex) => (
                      <div key={actionIndex}>{action}</div>
                    ))}
                  </TimelineActionItem>
                </TimelineActionsList>
              </TimelineText>
            </TimelineNodeRow>
          );
        })}
      </TimelineContainer>
    </TimelineWrapper>
  );
};
